/**
 * Firebase Saved For Later Service
 * Manages per-user saved items in Firestore (stores product IDs only)
 * Mirrors the wishlist.service.ts pattern
 */
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';

/**
 * Get saved-for-later product IDs for a user
 */
export const getSavedForLaterProductIds = async (userId: string): Promise<string[]> => {
  try {
    const docSnap = await getDoc(doc(db, COLLECTIONS.SAVED_FOR_LATER, userId));
    if (docSnap.exists()) {
      return (docSnap.data().productIds as string[]) || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching saved for later:', error);
    return [];
  }
};

/**
 * Add a product to the user's saved-for-later list
 */
export const addToSavedForLaterFB = async (userId: string, productId: string): Promise<void> => {
  try {
    await setDoc(
      doc(db, COLLECTIONS.SAVED_FOR_LATER, userId),
      {
        userId,
        productIds: arrayUnion(productId),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error adding to saved for later:', error);
  }
};

/**
 * Remove a product from the user's saved-for-later list
 */
export const removeFromSavedForLaterFB = async (userId: string, productId: string): Promise<void> => {
  try {
    await setDoc(
      doc(db, COLLECTIONS.SAVED_FOR_LATER, userId),
      {
        productIds: arrayRemove(productId),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error removing from saved for later:', error);
  }
};

/**
 * Set the full saved-for-later list (used for merge-on-login)
 */
export const setSavedForLaterFB = async (userId: string, productIds: string[]): Promise<void> => {
  try {
    await setDoc(
      doc(db, COLLECTIONS.SAVED_FOR_LATER, userId),
      {
        userId,
        productIds,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error setting saved for later:', error);
  }
};

/**
 * Clear the user's saved-for-later list
 */
export const clearSavedForLaterFB = async (userId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.SAVED_FOR_LATER, userId));
  } catch (error) {
    console.error('Error clearing saved for later:', error);
  }
};
